import AppStore from "@public/app/appstore.svg";
import PlayStore from "@public/app/playstore.svg";
import { STORE_LINKS } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { track } from "@services/analytics";
import { useTranslations } from "@services/strings";
import Image from "next/image";
import { useEffect, useState } from "react";

type Store = keyof typeof STORE_LINKS;

export const StoreBadge = ({
  store,
  placement,
  className,
}: {
  store: Store;
  /** Where on the page the badge sits — goes into analytics. */
  placement: string;
  className?: string;
}) => {
  const { t } = useTranslations();

  return (
    <a
      href={STORE_LINKS[store]}
      target="_blank"
      rel="noopener"
      onClick={() =>
        track({
          event: "Button Clicked",
          properties: { label: `App Store Button - ${store} - ${placement}` },
        })
      }
      className={cn(
        "inline-flex rounded-lg bg-black transition-opacity hover:opacity-90 focus-visible:outline-mint",
        className,
      )}
      aria-label={`${t("site.common.downloadAria")} — ${
        store === "apple"
          ? t("site.common.appStore")
          : t("site.common.googlePlay")
      }`}
    >
      <Image
        src={store === "apple" ? AppStore : PlayStore}
        alt=""
        height={44}
      />
    </a>
  );
};

/** Both store badges side by side. */
export const StoreBadges = ({
  placement,
  className,
}: {
  placement: string;
  className?: string;
}) => (
  <div className={cn("flex flex-wrap items-center gap-3", className)}>
    <StoreBadge store="apple" placement={placement} />
    <StoreBadge store="android" placement={placement} />
  </div>
);

/**
 * The store link that matches the visitor's device, or `#download` on
 * desktop where both badges are shown at the end of the page.
 */
export const useStoreLink = (): string => {
  const [href, setHref] = useState<string>("#download");

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) setHref(STORE_LINKS.apple);
    else if (/Android/i.test(ua)) setHref(STORE_LINKS.android);
  }, []);

  return href;
};
